/**
 * APIManager.tsx - API Connection Manager (Postman-like)
 * 
 * CHANGE HISTORY:
 * - 251102_1501 danielbeer: Create API Manager inspired by Hoppscotch
 * 
 * Full-featured API testing and management interface for OpCentral
 * Inspired by Hoppscotch (open-source Postman alternative)
 */

import React, { useState } from 'react';
import { 
  Send, Play, Save, Trash2, Copy, Plus, ChevronDown, 
  Clock, Star, Folder, Code, Eye, Settings
} from 'lucide-react';

export interface APIRequest {
  id: string;
  name: string;
  method: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE' | 'HEAD' | 'OPTIONS';
  url: string;
  headers: Record<string, string>;
  params: Record<string, string>;
  body?: string;
  bodyType?: 'json' | 'form' | 'raw' | 'none';
  auth?: {
    type: 'none' | 'bearer' | 'basic' | 'api-key';
    token?: string;
    username?: string;
    password?: string;
    key?: string;
    value?: string;
  };
}

export interface APIResponse {
  status: number;
  statusText: string;
  headers: Record<string, string>;
  body: string;
  time: number;
  size: number;
}

export interface APICollection {
  id: string;
  name: string;
  requests: APIRequest[];
}

export interface APIManagerProps {
  onSave?: (request: APIRequest) => void;
  onExecute?: (request: APIRequest) => Promise<APIResponse>;
  collections?: APICollection[];
}

export const APIManager: React.FC<APIManagerProps> = ({
  onSave,
  onExecute,
  collections = []
}) => {
  const [currentRequest, setCurrentRequest] = useState<APIRequest>({
    id: 'new',
    name: 'Untitled Request',
    method: 'GET',
    url: '',
    headers: {},
    params: {},
    bodyType: 'none'
  });

  const [response, setResponse] = useState<APIResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState<'params' | 'headers' | 'body' | 'auth'>('params');
  const [responseTab, setResponseTab] = useState<'body' | 'headers' | 'raw'>('body');

  const handleSendRequest = async () => {
    if (!onExecute) return;
    
    setLoading(true);
    try {
      const result = await onExecute(currentRequest);
      setResponse(result);
    } catch (error: any) {
      setResponse({
        status: 0,
        statusText: 'Error',
        headers: {},
        body: error.message,
        time: 0,
        size: 0
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSave = () => {
    if (onSave) {
      onSave(currentRequest);
    }
  };

  const addHeader = () => {
    setCurrentRequest(prev => ({
      ...prev,
      headers: { ...prev.headers, '': '' }
    }));
  };

  const updateHeader = (oldKey: string, newKey: string, value: string) => {
    setCurrentRequest(prev => {
      const headers = { ...prev.headers };
      if (oldKey !== newKey) {
        delete headers[oldKey];
      }
      headers[newKey] = value;
      return { ...prev, headers };
    });
  };

  const removeHeader = (key: string) => {
    setCurrentRequest(prev => {
      const headers = { ...prev.headers };
      delete headers[key];
      return { ...prev, headers };
    });
  };

  const addParam = () => {
    setCurrentRequest(prev => ({
      ...prev,
      params: { ...prev.params, '': '' }
    }));
  };

  const updateParam = (oldKey: string, newKey: string, value: string) => {
    setCurrentRequest(prev => {
      const params = { ...prev.params };
      if (oldKey !== newKey) {
        delete params[oldKey];
      }
      params[newKey] = value;
      return { ...prev, params };
    });
  };

  const removeParam = (key: string) => {
    setCurrentRequest(prev => {
      const params = { ...prev.params };
      delete params[key];
      return { ...prev, params };
    });
  };

  return (
    <div className="interfacr-api-manager">
      <div className="api-manager-layout">
        {/* Sidebar */}
        <div className="sidebar">
          <div className="sidebar-header">
            <h3>Collections</h3>
            <button className="btn-icon" title="New Collection">
              <Plus size={16} />
            </button>
          </div>
          
          <div className="collections-list">
            {collections.map(collection => (
              <div key={collection.id} className="collection-item">
                <Folder size={16} />
                <span>{collection.name}</span>
                <ChevronDown size={14} />
              </div>
            ))}
          </div>

          <div className="sidebar-section">
            <div className="section-header">
              <Clock size={16} />
              <span>History</span>
            </div>
          </div>

          <div className="sidebar-section">
            <div className="section-header">
              <Star size={16} />
              <span>Favorites</span>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="main-content">
          {/* Request Section */}
          <div className="request-section">
            <div className="request-header">
              <input
                type="text"
                className="request-name"
                value={currentRequest.name}
                onChange={(e) => setCurrentRequest(prev => ({ ...prev, name: e.target.value }))}
                placeholder="Request name"
              />
              <div className="request-actions">
                <button className="btn-icon" onClick={handleSave} title="Save">
                  <Save size={16} />
                </button>
                <button className="btn-icon" title="Duplicate">
                  <Copy size={16} />
                </button>
                <button className="btn-icon" title="Delete">
                  <Trash2 size={16} />
                </button>
              </div>
            </div>

            <div className="request-url-bar">
              <select
                className="method-select"
                value={currentRequest.method}
                onChange={(e) => setCurrentRequest(prev => ({ 
                  ...prev, 
                  method: e.target.value as APIRequest['method']
                }))}
              >
                <option value="GET">GET</option>
                <option value="POST">POST</option>
                <option value="PUT">PUT</option>
                <option value="PATCH">PATCH</option>
                <option value="DELETE">DELETE</option>
                <option value="HEAD">HEAD</option>
                <option value="OPTIONS">OPTIONS</option>
              </select>

              <input
                type="text"
                className="url-input"
                value={currentRequest.url}
                onChange={(e) => setCurrentRequest(prev => ({ ...prev, url: e.target.value }))}
                placeholder="Enter request URL"
              />

              <button
                className="btn-send"
                onClick={handleSendRequest}
                disabled={loading || !currentRequest.url}
              >
                {loading ? (
                  <>
                    <Play size={16} className="spinner" />
                    Sending...
                  </>
                ) : (
                  <>
                    <Send size={16} />
                    Send
                  </>
                )}
              </button>
            </div>

            {/* Request Tabs */}
            <div className="request-tabs">
              <button
                className={`tab ${activeTab === 'params' ? 'active' : ''}`}
                onClick={() => setActiveTab('params')}
              >
                Params
              </button>
              <button
                className={`tab ${activeTab === 'headers' ? 'active' : ''}`}
                onClick={() => setActiveTab('headers')}
              >
                Headers ({Object.keys(currentRequest.headers).length})
              </button>
              <button
                className={`tab ${activeTab === 'body' ? 'active' : ''}`}
                onClick={() => setActiveTab('body')}
              >
                Body
              </button>
              <button
                className={`tab ${activeTab === 'auth' ? 'active' : ''}`}
                onClick={() => setActiveTab('auth')}
              >
                Auth
              </button>
            </div>

            {/* Request Tab Content */}
            <div className="request-tab-content">
              {activeTab === 'params' && (
                <div className="params-section">
                  <div className="key-value-list">
                    {Object.entries(currentRequest.params).map(([key, value]) => (
                      <div key={key} className="key-value-row">
                        <input
                          type="text"
                          value={key}
                          onChange={(e) => updateParam(key, e.target.value, value)}
                          placeholder="Key"
                        />
                        <input
                          type="text"
                          value={value}
                          onChange={(e) => updateParam(key, key, e.target.value)}
                          placeholder="Value"
                        />
                        <button
                          className="btn-icon"
                          onClick={() => removeParam(key)}
                          title="Remove"
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                    ))}
                  </div>
                  <button className="btn-add" onClick={addParam}>
                    <Plus size={14} />
                    Add Parameter
                  </button>
                </div>
              )}

              {activeTab === 'headers' && (
                <div className="headers-section">
                  <div className="key-value-list">
                    {Object.entries(currentRequest.headers).map(([key, value]) => (
                      <div key={key} className="key-value-row">
                        <input
                          type="text"
                          value={key}
                          onChange={(e) => updateHeader(key, e.target.value, value)}
                          placeholder="Header"
                        />
                        <input
                          type="text"
                          value={value}
                          onChange={(e) => updateHeader(key, key, e.target.value)}
                          placeholder="Value"
                        />
                        <button
                          className="btn-icon"
                          onClick={() => removeHeader(key)}
                          title="Remove"
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                    ))}
                  </div>
                  <button className="btn-add" onClick={addHeader}>
                    <Plus size={14} />
                    Add Header
                  </button>
                </div>
              )}

              {activeTab === 'body' && (
                <div className="body-section">
                  <div className="body-type-selector">
                    <label>
                      <input
                                type="radio"
                        checked={currentRequest.bodyType === 'none'}
                        onChange={() => setCurrentRequest(prev => ({ ...prev, bodyType: 'none' }))}
                      />
                      None
                    </label>
                    <label>
                      <input
                        type="radio"
                        checked={currentRequest.bodyType === 'json'}
                        onChange={() => setCurrentRequest(prev => ({ ...prev, bodyType: 'json' }))}
                      />
                      JSON
                    </label>
                    <label>
                      <input
                        type="radio"
                        checked={currentRequest.bodyType === 'form'}
                        onChange={() => setCurrentRequest(prev => ({ ...prev, bodyType: 'form' }))}
                      />
                      Form Data
                    </label>
                    <label>
                      <input
                        type="radio"
                        checked={currentRequest.bodyType === 'raw'}
                        onChange={() => setCurrentRequest(prev => ({ ...prev, bodyType: 'raw' }))}
                      />
                      Raw
                    </label>
                  </div>

                  {currentRequest.bodyType !== 'none' && (
                    <textarea
                      className="body-editor"
                      value={currentRequest.body || ''}
                      onChange={(e) => setCurrentRequest(prev => ({ ...prev, body: e.target.value }))}
                      placeholder={`Enter ${currentRequest.bodyType} body`}
                    />
                  )}
                </div>
              )}

              {activeTab === 'auth' && (
                <div className="auth-section">
                  <select
                    value={currentRequest.auth?.type || 'none'}
                    onChange={(e) => setCurrentRequest(prev => ({
                      ...prev,
                      auth: { ...prev.auth, type: e.target.value as any }
                    }))}
                  >
                    <option value="none">No Auth</option>
                    <option value="bearer">Bearer Token</option>
                    <option value="basic">Basic Auth</option>
                    <option value="api-key">API Key</option>
                  </select>

                  {currentRequest.auth?.type === 'bearer' && (
                    <input
                      type="text"
                      placeholder="Token"
                      value={currentRequest.auth?.token || ''}
                      onChange={(e) => setCurrentRequest(prev => ({
                        ...prev,
                        auth: { ...prev.auth, type: 'bearer', token: e.target.value }
                      }))}
                    />
                  )}

                  {currentRequest.auth?.type === 'basic' && (
                    <>
                      <input
                        type="text"
                        placeholder="Username"
                        value={currentRequest.auth?.username || ''}
                        onChange={(e) => setCurrentRequest(prev => ({
                          ...prev,
                          auth: { ...prev.auth, type: 'basic', username: e.target.value }
                        }))}
                      />
                      <input
                        type="password"
                        placeholder="Password"
                        value={currentRequest.auth?.password || ''}
                        onChange={(e) => setCurrentRequest(prev => ({
                          ...prev,
                          auth: { ...prev.auth, type: 'basic', password: e.target.value }
                        }))}
                      />
                    </>
                  )}

                  {currentRequest.auth?.type === 'api-key' && (
                    <>
                      <input
                        type="text"
                        placeholder="Key"
                        value={currentRequest.auth?.key || ''}
                        onChange={(e) => setCurrentRequest(prev => ({
                          ...prev,
                          auth: { ...prev.auth, type: 'api-key', key: e.target.value }
                        }))}
                      />
                      <input
                        type="text"
                        placeholder="Value"
                        value={currentRequest.auth?.value || ''}
                        onChange={(e) => setCurrentRequest(prev => ({
                          ...prev,
                          auth: { ...prev.auth, type: 'api-key', value: e.target.value }
                        }))}
                      />
                    </>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Response Section */}
          {response && (
            <div className="response-section">
              <div className="response-header">
                <div className="response-status">
                  <span className={`status-badge status-${Math.floor(response.status / 100)}xx`}>
                    {response.status} {response.statusText}
                  </span>
                  <span className="response-time">{response.time}ms</span>
                  <span className="response-size">{(response.size / 1024).toFixed(2)} KB</span>
                </div>
              </div>

              <div className="response-tabs">
                <button
                  className={`tab ${responseTab === 'body' ? 'active' : ''}`}
                  onClick={() => setResponseTab('body')}
                >
                  <Eye size={14} />
                  Body
                </button>
                <button
                  className={`tab ${responseTab === 'headers' ? 'active' : ''}`}
                  onClick={() => setResponseTab('headers')}
                >
                  Headers ({Object.keys(response.headers).length})
                </button>
                <button
                  className={`tab ${responseTab === 'raw' ? 'active' : ''}`}
                  onClick={() => setResponseTab('raw')}
                >
                  <Code size={14} />
                  Raw
                </button>
              </div>

              <div className="response-content">
                {responseTab === 'body' && (
                  <pre className="response-body">{response.body}</pre>
                )}
                {responseTab === 'headers' && (
                  <div className="response-headers">
                    {Object.entries(response.headers).map(([key, value]) => (
                      <div key={key} className="header-row">
                        <strong>{key}:</strong> {value}
                      </div>
                    ))}
                  </div>
                )}
                {responseTab === 'raw' && (
                  <pre className="response-raw">
                    {`HTTP/${response.status} ${response.statusText}\n`}
                    {Object.entries(response.headers).map(([k, v]) => `${k}: ${v}`).join('\n')}
                    {'\n\n'}
                    {response.body}
                  </pre>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      <style>{`
        .interfacr-api-manager {
          width: 100%;
          height: 100vh;
          display: flex;
          flex-direction: column;
          background-color: #f9fafb;
          font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', sans-serif;
        }

        .api-manager-layout {
          display: flex;
          flex: 1;
          overflow: hidden;
        }

        /* Sidebar */
        .sidebar {
          width: 250px;
          background-color: white;
          border-right: 1px solid #e5e7eb;
          display: flex;
          flex-direction: column;
          overflow-y: auto;
        }

        .sidebar-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 1rem;
          border-bottom: 1px solid #e5e7eb;
        }

        .sidebar-header h3 {
          margin: 0;
          font-size: 0.875rem;
          font-weight: 600;
          color: #111827;
        }

        .collections-list {
          flex: 1;
        }

        .collection-item {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          padding: 0.5rem 1rem;
          cursor: pointer;
          transition: background-color 0.2s;
        }

        .collection-item:hover {
          background-color: #f3f4f6;
        }

        .sidebar-section {
          border-top: 1px solid #e5e7eb;
          padding: 0.5rem 0;
        }

        .section-header {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          padding: 0.5rem 1rem;
          font-size: 0.875rem;
          font-weight: 500;
          color: #6b7280;
        }

        /* Main Content */
        .main-content {
          flex: 1;
          display: flex;
          flex-direction: column;
          overflow: hidden;
        }

        /* Request Section */
        .request-section {
          flex: 1;
          display: flex;
          flex-direction: column;
          background-color: white;
          border-bottom: 1px solid #e5e7eb;
        }

        .request-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 1rem;
          border-bottom: 1px solid #e5e7eb;
        }

        .request-name {
          flex: 1;
          border: none;
          font-size: 1rem;
          font-weight: 600;
          outline: none;
        }

        .request-actions {
          display: flex;
          gap: 0.5rem;
        }

        .btn-icon {
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 0.5rem;
          background-color: transparent;
          border: none;
          border-radius: 0.25rem;
          color: #6b7280;
          cursor: pointer;
          transition: all 0.2s;
        }

        .btn-icon:hover {
          background-color: #f3f4f6;
          color: #111827;
        }

        .request-url-bar {
          display: flex;
          gap: 0.5rem;
          padding: 1rem;
          border-bottom: 1px solid #e5e7eb;
        }

        .method-select {
          padding: 0.5rem 1rem;
          border: 1px solid #d1d5db;
          border-radius: 0.375rem;
          font-weight: 600;
          background-color: white;
          cursor: pointer;
        }

        .url-input {
          flex: 1;
          padding: 0.5rem 1rem;
          border: 1px solid #d1d5db;
          border-radius: 0.375rem;
          font-size: 0.875rem;
        }

        .url-input:focus {
          outline: none;
          border-color: #3b82f6;
          box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
        }

        .btn-send {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          padding: 0.5rem 1.5rem;
          background-color: #3b82f6;
          color: white;
          border: none;
          border-radius: 0.375rem;
          font-weight: 500;
          cursor: pointer;
          transition: background-color 0.2s;
        }

        .btn-send:hover:not(:disabled) {
          background-color: #2563eb;
        }

        .btn-send:disabled {
          background-color: #9ca3af;
          cursor: not-allowed;
        }

        .spinner {
          animation: spin 1s linear infinite;
        }

        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }

        /* Tabs */
        .request-tabs,
        .response-tabs {
          display: flex;
          gap: 0.25rem;
          padding: 0 1rem;
          border-bottom: 1px solid #e5e7eb;
        }

        .tab {
          display: flex;
          align-items: center;
          gap: 0.25rem;
          padding: 0.75rem 1rem;
          background-color: transparent;
          border: none;
          border-bottom: 2px solid transparent;
          color: #6b7280;
          font-size: 0.875rem;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.2s;
        }

        .tab:hover {
          color: #111827;
        }

        .tab.active {
          color: #3b82f6;
          border-bottom-color: #3b82f6;
        }

        /* Tab Content */
        .request-tab-content {
          flex: 1;
          padding: 1rem;
          overflow-y: auto;
        }

        .key-value-list {
          display: flex;
          flex-direction: column;
          gap: 0.5rem;
          margin-bottom: 1rem;
        }

        .key-value-row {
          display: flex;
          gap: 0.5rem;
        }

        .key-value-row input {
          flex: 1;
          padding: 0.5rem;
          border: 1px solid #d1d5db;
          border-radius: 0.25rem;
          font-size: 0.875rem;
        }

        .btn-add {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          padding: 0.5rem 1rem;
          background-color: transparent;
          border: 1px dashed #d1d5db;
          border-radius: 0.25rem;
          color: #6b7280;
          font-size: 0.875rem;
          cursor: pointer;
          transition: all 0.2s;
        }

        .btn-add:hover {
          border-color: #3b82f6;
          color: #3b82f6;
        }

        .body-type-selector {
          display: flex;
          gap: 1rem;
          margin-bottom: 1rem;
        }

        .body-type-selector label {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          font-size: 0.875rem;
          cursor: pointer;
        }

        .body-editor {
          width: 100%;
          min-height: 200px;
          padding: 0.75rem;
          border: 1px solid #d1d5db;
          border-radius: 0.375rem;
          font-family: 'Monaco', 'Menlo', monospace;
          font-size: 0.875rem;
          resize: vertical;
        }

        .auth-section {
          display: flex;
          flex-direction: column;
          gap: 0.75rem;
        }

        .auth-section select,
        .auth-section input {
          padding: 0.5rem;
          border: 1px solid #d1d5db;
          border-radius: 0.25rem;
          font-size: 0.875rem;
        }

        /* Response Section */
        .response-section {
          flex: 1;
          display: flex;
          flex-direction: column;
          background-color: white;
          overflow: hidden;
        }

        .response-header {
          padding: 1rem;
          border-bottom: 1px solid #e5e7eb;
        }

        .response-status {
          display: flex;
          align-items: center;
          gap: 1rem;
        }

        .status-badge {
          padding: 0.25rem 0.75rem;
          border-radius: 9999px;
          font-size: 0.75rem;
          font-weight: 600;
        }

        .status-2xx {
          background-color: #dcfce7;
          color: #166534;
        }

        .status-3xx {
          background-color: #dbeafe;
          color: #1e40af;
        }

        .status-4xx {
          background-color: #fef3c7;
          color: #92400e;
        }

        .status-5xx {
          background-color: #fee2e2;
          color: #991b1b;
        }

        .response-time,
        .response-size {
          font-size: 0.875rem;
          color: #6b7280;
        }

        .response-content {
          flex: 1;
          padding: 1rem;
          overflow-y: auto;
        }

        .response-body,
        .response-raw {
          margin: 0;
          padding: 1rem;
          background-color: #f9fafb;
          border: 1px solid #e5e7eb;
          border-radius: 0.375rem;
          font-family: 'Monaco', 'Menlo', monospace;
          font-size: 0.875rem;
          overflow-x: auto;
        }

        .response-headers {
          display: flex;
          flex-direction: column;
          gap: 0.5rem;
        }

        .header-row {
          padding: 0.5rem;
          background-color: #f9fafb;
          border-radius: 0.25rem;
          font-size: 0.875rem;
        }

        .header-row strong {
          color: #111827;
          margin-right: 0.5rem;
        }
      `}</style>
    </div>
  );
};

export default APIManager;
